<template>
  <rest-page :fetch-endpoint="fetchEndpoints">
    <div class="rounds-predictions-form-ct" v-loading="isLoading">
      <div class="block">
        <div class="header">
          <div>
            {{
              $t('Gamification_RoundsPredictionsForm_PredefinedRoundDetails')
            }}
          </div>
          <div class="description">
            <span>
              {{
                $t(
                  'Gamification_RoundsPredictionsForm_PredefinedRoundDetailsDescription'
                )
              }}
            </span>
          </div>
        </div>
        <div class="content">
          <div class="section">
            <div>
              <span>{{
                $t('Gamification_RoundsPredictionsForm_NoOfRounds')
              }}</span
              ><br />
              <span class="description">{{
                $t('Gamification_RoundsPredictionsForm_NoOfRoundsDescription')
              }}</span>
            </div>
            <div class="value">{{ model.rounds.length }}</div>
          </div>
          <div class="section">
            <div>
              <span>{{
                $t('Gamification_RoundsPredictionsForm_PredictionType')
              }}</span
              ><br />
              <span class="description">{{
                $t(
                  'Gamification_RoundsPredictionsForm_PredictionTypeDescription'
                )
              }}</span>
            </div>
            <div class="value">
              {{
                $t(
                  'Gamification_RoundsPredictionsForm_PredictionType_Team_Advancement'
                )
              }}
            </div>
          </div>
          <div class="section">
            <div>
              <span>{{
                $t('Gamification_RoundsPredictionsForm_NoOfSelections')
              }}</span
              ><br />
              <span class="description">{{
                $t(
                  'Gamification_RoundsPredictionsForm_NoOfSelectionsDescription'
                )
              }}</span>
            </div>
            <div class="value">{{ total_selections }}</div>
          </div>
        </div>
      </div>

      <template v-if="isCreationMode">
        <div class="form-ct">
          <rest-form
            v-if="model"
            ref="rounds-predictions-form"
            :model="model"
            :save-endpoint="saveEndpoint"
            :disabled="isReadOnly"
            :after-save="afterSave"
            @on-error="onError()"
          >
            <div v-for="(round, index) in model.rounds">
              <form-section
                :title="$t('Gamification_RoundsPredictionsForm_NameOfRound')"
                :description="
                  $t(
                    'Gamification_RoundsPredictionsForm_NameOfRoundDescription'
                  )
                "
                :is-required="true"
              >
                <rest-form-item
                  :prop="`rounds.${index}.name`"
                  :label="$t('Gamification_RoundsPredictionsForm_Name')"
                  class="default-width"
                  :rules="roundNameRules"
                >
                  <el-input
                    v-model="model.rounds[index].name"
                    required
                    data-qa="round-name-input"
                  />
                </rest-form-item>
              </form-section>

              <form-section
                :title="$t('Gamification_RoundsPredictionsForm_StartAndEndAt')"
                :description="
                  $t(
                    'Gamification_RoundsPredictionsForm_StartAndEndAtDescription'
                  )
                "
                :is-required="true"
              >
                <div class="default-width date-time-ct">
                  <rest-form-item
                    class="flex-1"
                    :prop="`rounds.${index}.start_date`"
                    :label="$t('Gamification_RoundsPredictionsForm_StartsAt')"
                    :rules="roundStartDateRules(index)"
                  >
                    <el-date-picker
                      v-model="model.rounds[index].start_date"
                      type="datetime"
                      format="MMMM d yyyy HH:mm"
                      :picker-options="startPickerOptions(index)"
                      default-time="00:00:00"
                      required
                      data-qa="starts-date-picker"
                      @change="
                        handleOnDateChange($event, index, 'start_date', [
                          `rounds.${index}.end_date`,
                          `rounds.${index}.closes_at`
                        ])
                      "
                    />
                  </rest-form-item>
                  <rest-form-item
                    class="flex-1"
                    :prop="`rounds.${index}.end_date`"
                    :label="$t('Gamification_RoundsPredictionsForm_EndsAt')"
                    :rules="roundEndDateRules(index)"
                  >
                    <el-date-picker
                      v-model="model.rounds[index].end_date"
                      type="datetime"
                      format="MMMM d yyyy HH:mm"
                      :picker-options="endPickerOptions(index)"
                      default-time="23:59:59"
                      required
                      data-qa="ends-date-picker"
                      @change="
                        handleOnDateChange($event, index, 'end_date', [
                          `rounds.${index}.start_date`,
                          `rounds.${index}.closes_at`
                        ])
                      "
                    />
                  </rest-form-item>
                </div>
              </form-section>

              <form-section
                :title="$t('Gamification_RoundsPredictionsForm_ClosesAt')"
                :description="
                  $t('Gamification_RoundsPredictionsForm_ClosesAtDescription')
                "
                :is-required="true"
              >
                <rest-form-item
                  :prop="`rounds.${index}.closes_at`"
                  class="default-width"
                  :label="
                    $t('Gamification_RoundsPredictionsForm_SelectDateAndTime')
                  "
                  :rules="roundClosesAtRules(index)"
                >
                  <el-date-picker
                    v-model="model.rounds[index].closes_at"
                    type="datetime"
                    format="MMMM d yyyy HH:mm"
                    :picker-options="closesDatePickerOptions(index)"
                    default-time="23:59:59"
                    required
                    data-qa="closes-at-date-picker"
                    @change="
                      handleOnDateChange($event, index, 'closes_at', [
                        `rounds.${index}.start_date`,
                        `rounds.${index}.end_date`
                      ])
                    "
                  />
                </rest-form-item>
              </form-section>
            </div>
          </rest-form>
        </div>

        <div class="footer">
          <div class="footer-ct">
            <el-button
              type="primary"
              :disabled="isSubmitDisabled"
              @click="saveRoundsPredictions"
              data-qa="next-step-btn"
            >
              {{ $t('Gamification_RoundsPredictionsForm_SaveButton') }}
              <i class="el-icon-arrow-right el-icon-right"></i>
            </el-button>
          </div>
        </div>
      </template>

      <div v-else>
        <div class="action-buttons">
          <!-- TODO: Temporarily hidden — feature not yet available from the gamification backend -->
          <el-button
            v-if="false"
            type="primary"
            plain
            data-qa="view-audit-logs-btn"
            >{{
              $t('Gamification_RoundsPredictionsForm_Table_ViewAuditLogs')
            }}</el-button
          >
          <el-button
            @click="downloadRounds()"
            type="primary"
            data-qa="download-data-btn"
            :loading="downloadLoading"
            >{{
              $t('Gamification_RoundsPredictionsForm_Table_DownloadData')
            }}</el-button
          >
        </div>
        <el-table
          ref="rounds-scoring-table"
          :data="roundsScoringTableData"
          :border="true"
          stripe
        >
          <el-table-column
            prop="name"
            :label="$t('Gamification_RoundsPredictionsForm_Table_RoundName')"
            sortable
          />
          <el-table-column
            prop="start_date"
            :label="$t('Gamification_RoundsPredictionsForm_Table_StartDate')"
            sortable
            :formatter="displayDate"
          />
          <el-table-column
            prop="end_date"
            :label="$t('Gamification_RoundsPredictionsForm_Table_EndsDate')"
            sortable
            :formatter="displayDate"
          />
          <el-table-column
            prop="closes_at"
            :label="$t('Gamification_RoundsPredictionsForm_Table_ClosesAt')"
            sortable
            :formatter="displayDate"
          />
          <el-table-column
            prop="updated_at"
            :label="$t('Gamification_RoundsPredictionsForm_Table_Modified')"
            sortable
            :formatter="displayDate"
          />
        </el-table>
      </div>
    </div>
  </rest-page>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import moment from 'moment/moment'
import FormSection from '@/common/components/form/FormSection.vue'
import RestForm from '@/common/components/rest/RestForm.vue'
import RestFormItem from '@/common/components/rest/RestFormItem.vue'
import { castDate, checkIsButtonDisabled } from '@/modules/gamification/helpers'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum'
import _ from 'lodash'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'GameRoundsPredictionsForm',
  components: {
    RestPage,
    RestForm,
    FormSection,
    RestFormItem
  },
  data() {
    return {
      requiredFields: ['name', 'start_date', 'end_date', 'closes_at'],
      isLoading: false,
      model: {
        rounds: [
          {
            name: undefined,
            start_date: undefined,
            end_date: undefined,
            closes_at: undefined,
            updated_at: undefined
          }
        ]
      },
      total_selections: null,
      initialStateModel: undefined,
      downloadLoading: false
    }
  },
  props: {
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    isCompleted: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    roundsScoringTableData() {
      if (!this.model || !this.model.rounds) return []

      return this.model.rounds
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'put',
        url: `gamify/${this.$route.params.id}/rounds/${this.gameCampaignId}`
      })
    },
    roundNameRules() {
      return {
        validator: (rule, value, callback) => {
          if (typeof value === 'string' && value.trim() === '') {
            callback(
              new Error(
                this.$t(
                  'Gamification_RoundsPredictionsForm_RoundNameIsRequired'
                )
              )
            )
          } else {
            callback()
          }
        },
        trigger: 'blur'
      }
    },
    roundStartDateRules() {
      return index => [
        {
          required: true,
          message: this.$t(
            'Gamification_RoundsPredictionsForm_StartAtRequired'
          ),
          trigger: ['blur', 'change']
        },
        {
          validator: (_rule, value, callback) => {
            const round = this.model.rounds[index]
            const end = round.end_date

            if (!value || !end) {
              return callback()
            }

            if (moment(value).isSameOrAfter(moment(end))) {
              return callback(
                new Error(this.$t('Gamification_StartTimeValidation'))
              )
            }

            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    roundEndDateRules() {
      return index => [
        {
          required: true,
          message: this.$t('Gamification_RoundsPredictionsForm_EndAtRequired'),
          trigger: ['blur', 'change']
        },
        {
          validator: (_rule, value, callback) => {
            const round = this.model.rounds[index]
            const start = round.start_date

            if (!value || !start) return callback()

            if (moment(value).isSameOrBefore(moment(start))) {
              return callback(
                new Error(this.$t('Gamification_EndTimeValidation'))
              )
            }

            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    roundClosesAtRules() {
      return index => [
        {
          required: true,
          message: this.$t(
            'Gamification_RoundsPredictionsForm_ClosesAtRequired'
          ),
          trigger: ['blur', 'change']
        },
        {
          validator: (_rule, value, callback) => {
            const round = this.model.rounds[index]
            const start = round.start_date
            const end = round.end_date

            if (!value) return callback()

            if (start && moment(value).isSameOrBefore(moment(start))) {
              return callback(
                new Error(this.$t('Gamification_LockTimeStartTimeValidation'))
              )
            }

            if (end && moment(value).isSameOrAfter(moment(end))) {
              return callback(
                new Error(this.$t('Gamification_LockTimeEndTimeValidation'))
              )
            }

            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    isSubmitDisabled() {
      if (!this.model || !this.model.rounds) return true

      return (
        checkIsButtonDisabled(this.requiredFields, this.model.rounds) ||
        this.isLoading
      )
    },
    hasChanges() {
      return !_.isEqual(this.initialStateModel, this.model)
    },
    fetchEndpoints() {
      return [this.fetchRoundsEndpoint(), this.fetchSelectionsEndpoint()]
    }
  },
  mounted() {
    this.initialStateModel = _.cloneDeep(this.model)
  },
  watch: {
    isSubmitDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    },
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    saveFlag: {
      async handler() {
        await this.saveRoundsPredictions(false)
      }
    }
  },
  methods: {
    castDate,
    fetchRoundsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/rounds/${this.gameCampaignId}`,
        handler: data => {
          if (data && data['data']) {
            this.model.rounds = data['data'].map(round => ({
              id: round.id,
              name: round.name,
              start_date: round.start_date ? castDate(round.start_date) : null,
              end_date: round.end_date ? castDate(round.end_date) : null,
              closes_at: round.closes_at ? castDate(round.closes_at) : null,
              updated_at: round.updated_at ? castDate(round.updated_at) : null
            }))
          }
          this.initialStateModel = _.cloneDeep(this.model)
        }
      })
    },
    fetchSelectionsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/competition/selection`,
        handler: data => {
          if (data && data['data'] && data['data']['total_selections']) {
            this.total_selections = data['data']['total_selections']
          } else {
            this.total_selections = 0
          }
        }
      })
    },
    async saveRoundsPredictions(shouldGoToNextStep = true) {
      this.isLoading = true
      try {
        const valid = await this.$refs['rounds-predictions-form'].validate()

        if (valid) {
          this.$refs['rounds-predictions-form'].save()
          this.shouldGoToNextStep = shouldGoToNextStep
        }
      } catch (e) {
        console.error(e)
        this.isLoading = false
      }
    },
    afterSave(data, finishLoading) {
      this.initialStateModel = _.cloneDeep(this.model)
      finishLoading()
      this.isLoading = false

      if (this.shouldGoToNextStep) {
        this.$emit('goToNextStep', GameCreationSubSteps.ROUNDS_AND_PREDICTIONS)
      } else if (this.isCreationMode) {
        EventBus.$emit(
          'updateStep',
          GameCreationSubSteps.ROUNDS_AND_PREDICTIONS
        )
        this.$nextTick(() => {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
      }
    },
    onError() {
      this.isLoading = false
    },
    startPickerOptions(index) {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')
          const round = this.model.rounds[index]

          if (current.isBefore(now)) return true

          if (round.end_date) {
            return current.isAfter(moment(round.end_date).startOf('day'))
          }

          return false
        }
      }
    },
    endPickerOptions(index) {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const now = moment().startOf('day')
          const current = moment(date).startOf('day')
          const round = this.model.rounds[index]

          if (current.isBefore(now, 'day')) return true

          if (round.start_date) {
            return current.isBefore(moment(round.start_date).startOf('day'))
          }

          return false
        }
      }
    },
    closesDatePickerOptions(index) {
      return {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => {
          const current = moment(date).startOf('day')
          const now = moment().startOf('day')
          const round = this.model.rounds[index]
          const start = moment(round.start_date).startOf('day')
          const end = moment(round.end_date).startOf('day')

          if (current.isBefore(now, 'day')) return true
          if (!round.start_date || !round.end_date) return false
          if (current.isBefore(start, 'minute')) return true
          return current.isAfter(end, 'minute')
        }
      }
    },
    revalidateFields(fields, index) {
      if (!fields.length) return

      fields.forEach(field => {
        const modelField = field.split('.').pop()
        if (
          this.model &&
          this.model.rounds &&
          this.model.rounds[index] &&
          this.model.rounds[index][modelField]
        ) {
          this.$refs['rounds-predictions-form'].validateField(field)
        }
      })
    },
    displayDate(_row, _column, value) {
      return value || ''
    },
    handleOnDateChange(payload, index, modelField, fields) {
      if (
        this.model &&
        this.model.rounds &&
        this.model.rounds[index] &&
        this.model.rounds[index][modelField]
      ) {
        this.model.rounds[index][modelField] = castDate(payload)
      }
      this.revalidateFields(fields, index)
    },
    downloadRounds() {
      this.downloadLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/rounds/download`
        })
      )
        .then(({ data }) => {
          this.$message({
            message: data.message,
            type: 'success'
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.downloadLoading = false
        })
    }
  }
}
</script>

<style scoped>
.rounds-predictions-form-ct {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.block {
  font-size: 14px;
  font-weight: 600;
  color: #4b515f;
  border: 1px solid #dbdee7;
  background-color: #fcfcfd;

  .description {
    font-size: 12px;
    font-weight: 400;
    color: #5c6374;
  }

  .value {
    font-weight: 400;
  }

  .header {
    padding: 10px 24px;
  }

  .content {
    border-top: 1px solid #dbdee7;
    padding: 10px 24px;
    gap: 25px;
    display: flex;
    flex-direction: column;
  }

  .section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 80%;
  }
}
.action-buttons {
  float: right;
  margin-bottom: 12px;
}
.date-time-ct {
  display: flex;
  gap: 24px;
  align-items: center;
}
.default-width {
  width: 596px;
}

.flex-1 {
  flex: 1;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/game-rules/GameRoundsPredictionsForm.vue